import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Shared-rig contract (see the framing addendum): the light here is the
// buffer's own history; a rig would only pollute the loop.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'kaleido',
  family: 'shader',
  danceability: 0.82,
  // 1, as the brief asks - unlike echo.js, which was dropped to 0 so the web
  // radio could see it at all. The consequence is the same one that change
  // was made to undo: the radio page measures tier 0 on its real hardware, so
  // Kaleido will not be OFFERED there. The frame-time governor can still put
  // it at tier 0 once selected elsewhere, which is why TIER[0] below is a real
  // layout and not a hole.
  requiresTier: 1,
  energyLevel: 0.78,
  bpmMin: 100,
  bpmMax: 170,
  movement: 0.80,
  tonality: 0.70,
  prefers: { gridStrength: [0.50, 1.0] },
  enabled: true
};

// ============================================================================
// Kaleido: an analogue video kaleidoscope. Echo's feedback loop with one thing
// inserted into it - a radial symmetry applied BEFORE the image is fed back.
//
//   Echo:    read(previous) -> transform -> write
//   Kaleido: read(previous) -> transform -> SYMMETRISE -> write
//
// The symmetry therefore ACCUMULATES. An order-6 pattern fed back becomes an
// order-6 pattern inside each of its own six sectors, and again the next pass:
// that recursion is where the fractal density comes from, and it is the whole
// reason the order of operations above is written down.
//
// EVERYTHING ELSE IS ECHO'S, DELIBERATELY UNCHANGED: the FeedbackBuffer, the
// three cumulative stability guards (decay < 1, a constant floor subtraction,
// a hard ceiling), the automatic luminance governor with its hysteresis, the
// masked dither, the 1.2s fade at a track change, and the rule that all render
// target work happens inside the display quad's onBeforeRender - the one place
// a party-mode visualizer may legally reach the renderer. None of it is
// rewritten here, because none of it was wrong.
//
// THE SEAM IS THE WHOLE DIFFICULTY. A symmetry computed carelessly leaves
// visible lines where its sectors meet, and in a feedback loop those lines are
// re-read, re-folded and re-amplified every frame until a parasitic star
// dominates the image. Three things are needed and all three are here:
//
//   1. highp. atan() in mediump is the single biggest source of seams: on
//      fp16 the angle near a sector boundary quantises coarsely enough that
//      neighbouring pixels fold to visibly different places. Measured, and
//      ?kaleidoprec=mediump exists so it can be measured again.
//   2. C1 continuity, not just C0. The brief's abs() fold is continuous in
//      VALUE at a boundary but its derivative flips sign, which reads as a
//      crease. softAbs() rounds both mirror lines over a fixed angle.
//   3. The centre is a singularity - theta is undefined there - so inside a
//      few pixels of it the fold is abandoned and the sectors are averaged.
//
// ?kaleidoseam=1 draws the sector boundaries in red over the output. Nothing
// in the image may line up with them.
// ============================================================================

// --- the loop (Echo's numbers, unchanged) -----------------------------------
const DECAY_DEFAULT = 0.955;
const DECAY_MIN = 0.85;
const DECAY_MAX = 0.975;      // never >= 1, whatever the governor does
const FLOOR_SUB = 0.004;      // weak pixels die instead of pooling into grey
const CEILING = 0.92;         // bloom catches injected content, never the loop
const GOVERN_EVERY_FRAMES = 30;
const GOVERN_HIGH = 0.35;
const GOVERN_RESUME = 0.25;
const GOVERN_LOW = 0.05;
const GOVERN_STEP = 0.01;
const LUM_SIZE = 8;
const TRACK_FADE_S = 1.2;
const FADE_DECAY = 0.96;
const BAND_TAU = 0.15;

// --- the symmetry -------------------------------------------------------------
// 3 and 5 are organic and unstable, 4 and 8 crystalline, 6 the classic
// kaleidoscope, 12 almost a mandala.
const ALL_ORDERS = [3, 4, 5, 6, 8, 12];
// progress decides which are available: the piece opens and closes on the two
// orders that read as "a kaleidoscope" and spends its middle on everything.
const CALM_ORDERS = [4, 6];
const DROP_ORDER = 12;
// The seam softening, in degrees of theta either side of every mirror line.
const SEAM_SOFT_DEG = 1.5;
// Radius, in buffer heights, inside which the fold is abandoned for an average
// of the sectors. About three pixels at a half-resolution 1080p buffer.
const CORE_R = 3.0 / 540.0;

// --- the transforms, continuous and locked to the grid -----------------------
// zoom > 1 pulls history TOWARD the centre (the sampled UV moves outward), so
// the beat digs the pattern inward and the tail of the beat releases it.
const ZOOM_BEAT = 1.012;
const ZOOM_REST = 0.979;
// 2.0, not the 4.5 first used. The mean of ZOOM_REST + (ZOOM_BEAT-ZOOM_REST)
// *exp(-k*beatPhase) over a beat is ZOOM_REST + (ZOOM_BEAT-ZOOM_REST)*(1-e^-k)/k,
// and at k=4.5 that is 0.993 - BELOW ONE, so the pattern drifted outward every
// beat, escaped the frame and left a black hole where the mandala should dig
// in. Rendered, it was a thin donut. k=2.0, with REST below 1, puts the mean at
// 0.993: the beat digs inward and the rest of the beat gives it back and a
// little more, so matter flows steadily OUTWARD from the centre.
// A mean of exactly 1.000 was the second mistake: with no radial flow the
// centre is a fixed point that only ever reads itself, so CORE_ATTEN and
// FLOOR_SUB grind it to black and the frame is a ring round a hole. The cover
// is injected at the centre and has to be able to travel out of it.
const ZOOM_DECAY = 2.0;
const ZOOM_BAND_SWING = 0.010;     // bands.low, on the depth of the breath
const ROT_STEPS_PER_SECTOR = 4;    // one notch of sector/4 per bar
const ROT_GLIDE_BEATS = 1;
const CENTRE_DRIFT = 0.02;
const CENTRE_PERIOD_S = 41;
// The radial attenuation the symmetry makes necessary: a pixel at the centre
// receives the sum of uOrder sectors, so the middle runs hotter than the rim
// and would otherwise clip to white. See the STABILITY note in the header.
// 0.985 over a 0.05 radius, not 0.94 over 0.15: the attenuator only has to
// cancel the uOrder-fold pile-up in the few pixels where the sectors actually
// meet. At 0.15 it was scrubbing a fifth of the picture.
const CORE_ATTEN = 0.985;
const CORE_ATTEN_R = 0.05;

// --- hue ------------------------------------------------------------------------
// Stronger than Echo's: successive petals come out in different hues and the
// accumulation lays down concentric rings of colour.
const HUE_SHIFT = 0.006;

// --- injection ------------------------------------------------------------------
// The cover is the source of ALL the matter, fed back continuously and faintly.
// LARGE, and it can be, because the injection is itself folded (see the
// feedback shader): the wedge crosses the cover at every radius, so the whole
// frame receives symmetrised cover rather than a halo round a pasted square.
const ART_SIZE = 0.80;             // of buffer height
const ART_OPACITY = 0.08;
// Slightly off centre: dead centre, the symmetry maps the cover onto itself and
// the result is trivial. Off centre, the wedge crosses a different part of it
// as the rotation steps round.
const ART_OFFSET = 0.25 * 0.5;
const ART_RESEED_OPACITY = 0.40;   // one frame, on the bar
const RESEED_LIFE_S = 0.05;
const SHAPE_LIFE_S = 0.12;
// What the last shape is held at between beats when there is no cover, so the
// loop always has a source. Faint enough that it reads as an afterglow.
const SHAPE_HOLD_OPACITY = 0.10;

const TIER = {
  // Tier 0 is a live-downgrade refuge only - requiresTier keeps the
  // orchestrator from offering Kaleido there, but the frame-time governor can
  // drop the tier mid-composition and the rebuild has to land somewhere.
  0: { scale: 0.25, orders: [4, 6, 8], seamSoft: false, chroma: false },
  1: { scale: 0.25, orders: [4, 6, 8], seamSoft: false, chroma: false },
  2: { scale: 0.5, orders: ALL_ORDERS, seamSoft: true, chroma: true }
};
const DEFAULT_TIER = 2;

// --- the display ------------------------------------------------------------------
// A kaleidoscope is looked at down a tube: the image is round and the corners
// of the frame are black.
const VIGNETTE_R = 0.88;
const VIGNETTE_FEATHER = 0.08;
const CHROMA = 0.006;              // the prism's dispersion at the rim

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// Hue rotation as an RGB matrix (rotation about the grey axis) - an HSL
// round-trip per pixel would cost more than the whole feedback shader.
function hueMatrix(angle, out) {
  const c = Math.cos(angle), s = Math.sin(angle);
  const a = 1 / 3, b = Math.sqrt(1 / 3);
  out.set(
    c + (1 - c) * a, a * (1 - c) - b * s, a * (1 - c) + b * s,
    a * (1 - c) + b * s, c + (1 - c) * a, a * (1 - c) - b * s,
    a * (1 - c) - b * s, a * (1 - c) + b * s, c + (1 - c) * a
  );
  return out;
}

const fullscreenVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// ============================================================================
// The feedback pass. Echo's, with the fold inserted between the transform and
// the sample.
// ============================================================================
function feedbackFragmentShader(precision, seamSoft) {
  return `
  precision ${precision} float;

  uniform sampler2D uPrevious;
  uniform float uZoom;
  uniform float uRotation;
  uniform float uOrder;
  uniform float uSeamSoft;     // radians, either side of each mirror line
  uniform vec2 uCentre;        // in aspect-corrected units, from the middle
  uniform float uAspect;
  uniform sampler2D uSource;   // the injection, symmetrised with everything else
  uniform float uDecay;
  uniform mat3 uHueMat;
  uniform float uSeed;

  varying vec2 vUv;

  const float PI = 3.141592653589793;

  // abs(), with its corner rounded over +-e. The plain abs() the brief gives
  // is continuous in VALUE across a mirror line but its derivative flips sign
  // there, and a derivative discontinuity in a feedback loop is re-read and
  // re-sharpened every frame until it is a crease. This is C1: it matches
  // abs() outside +-e, and inside it is the parabola whose slope reaches +-1
  // exactly at the join.
  float softAbs(float x, float e) {
    float a = abs(x);
    return a >= e ? a : (x * x / (2.0 * e) + e * 0.5);
  }

  // The triangle wave that does the folding, in [0,1], with BOTH of its
  // corners rounded: the one at the sector's midline and the one at its
  // boundary. Rounding only the first leaves exactly the seam this file is
  // about.
  float foldTri(float u, float e) {
    float t = 2.0 * u - 1.0;
    ${seamSoft ? `
    float f = softAbs(t, e);
    return 1.0 - softAbs(1.0 - f, e);` : `
    return abs(t);`}
  }

  void main() {
    // Aspect-corrected, or the sectors are elliptical and no amount of
    // softening makes them meet.
    vec2 q = (vUv - 0.5) * vec2(uAspect, 1.0) - uCentre;
    float r = length(q);

    float sector = 2.0 * PI / uOrder;
    float theta = atan(q.y, q.x);
    // u in [0,1) across one sector; the fold takes it to [0,1] mirrored.
    float u = fract(theta / sector);
    // The softening is expressed in theta and converted into u's own units:
    // one unit of the triangle's argument is half a sector.
    float e = uSeamSoft / (sector * 0.5);
    float a = foldTri(u, e) * sector * 0.5 + uRotation;

    // The transform, applied to the FOLDED position: folding first and
    // transforming after is the coordinate-space statement of "transform the
    // image, then symmetrise it".
    vec2 p = r * uZoom * vec2(cos(a), sin(a)) + uCentre;
    vec2 suv = p / vec2(uAspect, 1.0) + 0.5;
    vec3 prev = texture2D(uPrevious, suv).rgb;

    // The centre is a singularity - theta is meaningless there and adjacent
    // pixels fold to unrelated places, which the loop turns into a hot speck.
    // Inside CORE_R the sectors are averaged instead.
    if (r < ${CORE_R.toFixed(6)}) {
      vec3 acc = vec3(0.0);
      for (int i = 0; i < 4; i++) {
        float aa = a + float(i) * (PI * 0.5);
        vec2 cp = ${CORE_R.toFixed(6)} * vec2(cos(aa), sin(aa)) + uCentre;
        acc += texture2D(uPrevious, cp / vec2(uAspect, 1.0) + 0.5).rgb;
      }
      prev = mix(acc * 0.25, prev, smoothstep(0.0, ${CORE_R.toFixed(6)}, r));
    }

    prev = uHueMat * prev;

    // The symmetry concentrates energy inward: the middle receives the sum of
    // uOrder sectors, so it is decayed slightly harder than the rim. Without
    // this the governor spends the whole track fighting a white core.
    prev *= mix(${CORE_ATTEN.toFixed(3)}, 1.0, smoothstep(0.0, ${CORE_ATTEN_R.toFixed(3)}, r));

    // STABILITY - Echo's three guards, cumulative and unchanged.
    prev *= uDecay;
    prev -= vec3(${FLOOR_SUB.toFixed(4)});
    prev = max(prev, 0.0);

    // THE INJECTION, sampled at the FOLDED coordinate, inside the loop.
    // Drawn over the top of the fold instead (Echo's arrangement, which was
    // the first thing tried here), the cover stays a hard-edged SQUARE that
    // is the one thing in the frame not obeying the symmetry - and every
    // pixel away from it has no source at all, so FLOOR_SUB kills it in about
    // 35 frames and the picture is a small halo round the cover. Folding the
    // source makes it symmetric for free and gives every pixel a source.
    // Before the ceiling, so the injection is bounded by it too.
    prev += texture2D(uSource, suv).rgb;
    prev = min(prev, vec3(${CEILING.toFixed(3)}));

    // Echo's masked dither, unchanged: a feedback loop recirculates its own
    // quantisation error and bands within seconds without it, and it is masked
    // on black so the negative half cannot pool into a grey veil.
    vec2 fcw = mod(floor(gl_FragCoord.xy) + uSeed, 64.0);
    float dith = (fract(dot(fcw, vec2(0.7548776662, 0.5698402909))) - 0.5) / 255.0;
    float sig = step(3.5 / 255.0, max(prev.r, max(prev.g, prev.b)));

    gl_FragColor = vec4(prev + dith * sig, 1.0);
  }
`;
}

// ============================================================================
// The display pass: the buffer, down a tube.
// ============================================================================
function displayFragmentShader(chroma) {
  return `
  precision highp float;
  uniform sampler2D uBuffer;
  uniform float uAspect;
  uniform vec2 uCentre;
  uniform float uOrder;
  uniform float uRotation;
  uniform float uSeamDebug;
  varying vec2 vUv;
  const float PI = 3.141592653589793;
  void main() {
    vec2 q = (vUv - 0.5) * vec2(uAspect, 1.0);
    float r = length(q) / 0.5;            // 1.0 at the half-height

    ${chroma ? `
    // A real kaleidoscope's prism disperses at the rim: the three channels are
    // sampled at very slightly different radii, and only where the vignette is
    // already closing so the middle of the image stays sharp.
    float disp = ${CHROMA.toFixed(4)} * smoothstep(0.45, 1.0, r);
    vec2 dir = r > 1e-5 ? q / length(q) : vec2(0.0);
    vec2 off = dir * disp / vec2(uAspect, 1.0);
    vec3 col = vec3(
      texture2D(uBuffer, vUv + off).r,
      texture2D(uBuffer, vUv).g,
      texture2D(uBuffer, vUv - off).b);` : `
    vec3 col = texture2D(uBuffer, vUv).rgb;`}

    // The tube. Round image, black corners.
    float vig = 1.0 - smoothstep(${VIGNETTE_R.toFixed(3)},
                                 ${(VIGNETTE_R + VIGNETTE_FEATHER).toFixed(3)}, r);
    col *= vig;

    if (uSeamDebug > 0.5) {
      // The sector boundaries, drawn OVER the output and never fed back.
      // Nothing in the image may line up with these.
      float sector = 2.0 * PI / uOrder;
      vec2 c = q - uCentre;
      float theta = atan(c.y, c.x) - uRotation;
      float u = fract(theta / sector);
      float d = min(u, 1.0 - u) * sector;         // angle to the nearest boundary
      float w = 0.0035 / max(length(c), 0.02);
      col = mix(col, vec3(1.0, 0.0, 0.0), (1.0 - smoothstep(0.0, w, d)) * 0.85);
    }

    gl_FragColor = vec4(col, 1.0);
  }
`;
}

// The injected shapes are ANALYTIC on plain quads, as in Echo: the chain runs
// antialias:false over a half-resolution buffer, so a tessellated edge becomes
// a two-pixel staircase that the loop then compounds. uPx is the real buffer
// pixel in ortho units, computed on the CPU - deriving it from fwidth() would
// mean depending on GL_OES_standard_derivatives, which this codebase avoids.
const shapeVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const barFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform vec2 uHalfSize;
  uniform vec2 uRect;
  uniform float uPx;
  varying vec2 vUv;
  void main() {
    vec2 p = abs((vUv - 0.5) * 2.0 * uHalfSize);
    float w = max(uPx * 1.5, 1e-5);
    float a = clamp((uRect.x - p.x) / w, 0.0, 1.0) * clamp((uRect.y - p.y) / w, 0.0, 1.0);
    gl_FragColor = vec4(uColor * (a * uOpacity), 1.0);
  }
`;

const arcFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform vec2 uHalfSize;
  uniform float uInner;
  uniform float uOuter;
  uniform float uSpan;      // half-angle of the arc, radians
  uniform float uPx;
  varying vec2 vUv;
  void main() {
    vec2 p = (vUv - 0.5) * 2.0 * uHalfSize;
    float r = length(p);
    float w = max(uPx * 1.5, 1e-5);
    float a = clamp((uOuter - r) / w, 0.0, 1.0) * clamp((r - uInner) / w, 0.0, 1.0);
    // The arc is centred on +x in its own quad; the mesh is rotated into the
    // wedge by the CPU.
    float th = abs(atan(p.y, p.x));
    a *= clamp((uSpan - th) * r / w, 0.0, 1.0);
    gl_FragColor = vec4(uColor * (a * uOpacity), 1.0);
  }
`;

const triFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform vec2 uHalfSize;
  uniform float uSide;
  uniform float uPx;
  varying vec2 vUv;
  void main() {
    vec2 p = (vUv - 0.5) * 2.0 * uHalfSize;
    float w = max(uPx * 1.5, 1e-5);
    // Equilateral, pointing +x, as three half-plane distances.
    float k = 0.8660254;
    float d = max(max(-p.x - uSide * 0.5, p.x * 0.5 + p.y * k - uSide * 0.5),
                  p.x * 0.5 - p.y * k - uSide * 0.5);
    float a = clamp(-d / w, 0.0, 1.0);
    gl_FragColor = vec4(uColor * (a * uOpacity), 1.0);
  }
`;

export class KaleidoVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    const forcedOrder = parseInt(urlParam('kaleidoorder'), 10);
    this._forcedOrder = ALL_ORDERS.indexOf(forcedOrder) !== -1 ? forcedOrder : null;
    const forcedDecay = parseFloat(urlParam('kaleidodecay'));
    this._forcedDecay = Number.isFinite(forcedDecay) ? forcedDecay : null;
    this._seamDebug = urlParam('kaleidoseam') === '1';
    this._lumDebug = urlParam('kaleidolum') === '1';
    // Criterion 7 exists to be demonstrated, not asserted: this forces the
    // polar maths back to mediump so the seams can be seen and measured.
    this._precision = urlParam('kaleidoprec') === 'mediump' ? 'mediump' : 'highp';
  }

  init(scene, camera) {
    this._scene = scene;
    const layout = TIER[this._tier];
    this._layout = layout;
    this._buffer = new FeedbackBuffer({ scale: layout.scale });
    // The injection gets its own target so the feedback pass can sample it at
    // the folded coordinate. Same scale as the buffer, one target, no history.
    this._source = new FeedbackBuffer({ scale: layout.scale, single: true });

    // --- feedback pass ---
    this._feedbackUniforms = {
      uPrevious: { value: null },
      uZoom: { value: 1.0 },
      uRotation: { value: 0 },
      uOrder: { value: this._forcedOrder || 6 },
      uSeamSoft: { value: layout.seamSoft ? SEAM_SOFT_DEG * Math.PI / 180 : 0 },
      uCentre: { value: new THREE.Vector2(0, 0) },
      uSource: { value: null },
      uAspect: { value: 16 / 9 },
      uDecay: { value: this._forcedDecay != null ? this._forcedDecay : DECAY_DEFAULT },
      uHueMat: { value: hueMatrix(0, new THREE.Matrix3()) },
      uSeed: { value: 0 }
    };
    this._feedbackScene = new THREE.Scene();
    this._feedbackQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._feedbackUniforms,
        vertexShader: fullscreenVertexShader,
        fragmentShader: feedbackFragmentShader(this._precision, layout.seamSoft),
        depthTest: false, depthWrite: false
      })
    );
    this._feedbackQuad.frustumCulled = false;
    this._feedbackScene.add(this._feedbackQuad);

    // --- injection pass (drawn into the same target, no clear) ---
    this._injectScene = new THREE.Scene();
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;

    this._artTexture = null;
    this._artMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        transparent: true, opacity: ART_OPACITY,
        blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
      })
    );
    this._artMesh.scale.set(ART_SIZE, ART_SIZE, 1);
    this._artMesh.visible = false;
    this._injectScene.add(this._artMesh);

    this._shapeMaterials = [];
    this._bar = this._makeBar(0.16, 0.012);
    this._arc = this._makeArc(0.20, 0.235, 0.32);
    this._tri = this._makeTri(0.09);
    this._shapes = [this._bar, this._arc, this._tri];
    for (const m of this._shapes) { m.visible = false; this._injectScene.add(m); }

    // --- governor downsample target (Echo's, unchanged) ---
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_SIZE, LUM_SIZE, {
      minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
      depthBuffer: false, stencilBuffer: false
    });
    this._lumScene = new THREE.Scene();
    this._lumUniforms = { uBuffer: { value: null } };
    this._lumQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._lumUniforms,
        vertexShader: fullscreenVertexShader,
        fragmentShader: 'precision mediump float;\nuniform sampler2D uBuffer;\nvarying vec2 vUv;\n'
          + 'void main() { gl_FragColor = vec4(texture2D(uBuffer, vUv).rgb, 1.0); }',
        depthTest: false, depthWrite: false
      })
    );
    this._lumQuad.frustumCulled = false;
    this._lumScene.add(this._lumQuad);
    this._lumPixels = new Uint8Array(LUM_SIZE * LUM_SIZE * 4);

    // --- display quad in the MAIN scene, before the shared bloom ---
    this._displayUniforms = {
      uBuffer: { value: null },
      uAspect: { value: 16 / 9 },
      uCentre: this._feedbackUniforms.uCentre,
      uOrder: this._feedbackUniforms.uOrder,
      uRotation: this._feedbackUniforms.uRotation,
      uSeamDebug: { value: this._seamDebug ? 1 : 0 }
    };
    this._displayMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._displayUniforms,
        vertexShader: fullscreenVertexShader,
        fragmentShader: displayFragmentShader(layout.chroma),
        depthTest: false, depthWrite: false
      })
    );
    this._displayMesh.frustumCulled = false;
    this._displayMesh.renderOrder = -1000;
    const self = this;
    this._displayMesh.onBeforeRender = function (renderer) { self._runPasses(renderer); };
    scene.add(this._displayMesh);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._decay = this._forcedDecay != null ? this._forcedDecay : DECAY_DEFAULT;
    // DECAY_MAX, where Echo uses DECAY_DEFAULT. Echo caps the governor's
    // upward travel at the decay it started from, so the governor can only
    // ever reduce - which is right for a near-identity transform that always
    // has plenty of gain. A FOLD discards all but 1/order of the plane every
    // pass, and how much it discards depends on the group: measured over the
    // same 31 s, pmm settled at frame mean 0.17 and p3m1 at 0.029, below the
    // governor's own GOVERN_LOW. Letting it climb to DECAY_MAX is what the
    // governor is for, and 0.975 is still below 1, still under the floor
    // subtraction and still under the hard ceiling.
    this._decayCeiling = Math.max(this._decay, DECAY_MAX);
    this._governorHot = false;
    this._frameCount = 0;
    this._lastLum = 0;
    this._order = this._forcedOrder || 6;
    this._rotation = 0;
    this._rotFrom = 0;
    this._rotTo = 0;
    this._rotT = 1;
    this._rotDur = 0.5;
    this._lastPhraseIndex = null;
    this._lastBeatIndex = null;
    this._lastBarIndex = null;
    this._lowSmooth = 0.5;
    this._highSmooth = 0.5;
    this._hueAngle = 0;
    this._hueDir = 1;
    this._palette = [new THREE.Color(0x39a0ff), new THREE.Color(0xff3ba7), new THREE.Color(0xffd23b)];
    this._lastPaletteRef = undefined;
    this._lastTrackId = undefined;
    this._lastArtworkUrl = undefined;
    this._coldStart = true;
    this._nextSeedAt = 0;
    this._fadeUntil = -1;
    this._fading = false;
    this._shapeUntil = -1;
    this._reseedUntil = -1;
    this._dropUntil = -1;
    this._dropOrderBefore = null;
    this._bpm = null;
    this._beatPhase = 0;

    if (this._lumDebug) {
      const el = document.createElement('div');
      el.style.cssText = 'position:fixed;left:10px;top:10px;z-index:99999;'
        + 'font:12px/1.5 ui-monospace,monospace;color:#9fe;background:rgba(0,0,0,.65);'
        + 'padding:6px 9px;border-radius:6px;white-space:pre';
      document.body.appendChild(el);
      this._lumEl = el;
    }
  }

  _makeBar(halfX, halfY) {
    const m = 0.02;
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0xffffff) }, uOpacity: { value: 1 },
        uHalfSize: { value: new THREE.Vector2(halfX + m, halfY + m) },
        uRect: { value: new THREE.Vector2(halfX, halfY) },
        uPx: { value: 1 / 360 }
      },
      vertexShader: shapeVertexShader, fragmentShader: barFragmentShader,
      blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false
    });
    this._shapeMaterials.push(mat);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry((halfX + m) * 2, (halfY + m) * 2), mat);
    mesh.frustumCulled = false;
    return mesh;
  }

  _makeArc(inner, outer, span) {
    const half = outer * 1.15;
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0xffffff) }, uOpacity: { value: 1 },
        uHalfSize: { value: new THREE.Vector2(half, half) },
        uInner: { value: inner }, uOuter: { value: outer }, uSpan: { value: span },
        uPx: { value: 1 / 360 }
      },
      vertexShader: shapeVertexShader, fragmentShader: arcFragmentShader,
      blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false
    });
    this._shapeMaterials.push(mat);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(half * 2, half * 2), mat);
    mesh.frustumCulled = false;
    return mesh;
  }

  _makeTri(side) {
    const half = side * 0.9;
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0xffffff) }, uOpacity: { value: 1 },
        uHalfSize: { value: new THREE.Vector2(half, half) },
        uSide: { value: side }, uPx: { value: 1 / 360 }
      },
      vertexShader: shapeVertexShader, fragmentShader: triFragmentShader,
      blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false
    });
    this._shapeMaterials.push(mat);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(half * 2, half * 2), mat);
    mesh.frustumCulled = false;
    return mesh;
  }

  // Echo's governor, unchanged apart from the log prefix.
  _measureLuminance(renderer) {
    this._lumUniforms.uBuffer.value = this._buffer.write.texture;
    renderer.setRenderTarget(this._lumTarget);
    renderer.render(this._lumScene, this._ortho);
    renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_SIZE, LUM_SIZE, this._lumPixels);
    let sum = 0;
    for (let i = 0; i < this._lumPixels.length; i += 4) {
      sum += 0.299 * this._lumPixels[i] + 0.587 * this._lumPixels[i + 1] + 0.114 * this._lumPixels[i + 2];
    }
    const lum = sum / (255 * LUM_SIZE * LUM_SIZE);
    this._lastLum = lum;

    if (lum > GOVERN_HIGH) this._governorHot = true;
    if (this._governorHot) {
      if (lum > GOVERN_RESUME) {
        const before = this._decay;
        this._decay = Math.max(DECAY_MIN, this._decay - GOVERN_STEP);
        if (this._decay !== before) {
          // eslint-disable-next-line no-console
          console.log('[kaleido] governor: mean luminance ' + lum.toFixed(3)
            + ' -> decay ' + before.toFixed(3) + ' -> ' + this._decay.toFixed(3));
        }
      } else {
        this._governorHot = false;
      }
    } else if (lum < GOVERN_LOW && this._decay < Math.min(this._decayCeiling, DECAY_MAX)) {
      const before = this._decay;
      this._decay = Math.min(Math.min(this._decayCeiling, DECAY_MAX), this._decay + GOVERN_STEP);
      // eslint-disable-next-line no-console
      console.log('[kaleido] governor: mean luminance ' + lum.toFixed(3)
        + ' -> decay ' + before.toFixed(3) + ' -> ' + this._decay.toFixed(3));
    }
  }

  _runPasses(renderer) {
    const rebuilt = this._buffer.ensureSize(renderer);
    this._source.ensureSize(renderer);
    if (rebuilt) {
      this._displayUniforms.uBuffer.value = this._buffer.read.texture;
      const aspect = this._buffer.width / Math.max(1, this._buffer.height);
      this._ortho.left = -0.5 * aspect;
      this._ortho.right = 0.5 * aspect;
      this._ortho.updateProjectionMatrix();
      this._feedbackUniforms.uAspect.value = aspect;
      this._displayUniforms.uAspect.value = aspect;
      const px = 1 / Math.max(1, this._buffer.height);
      for (const mat of this._shapeMaterials) mat.uniforms.uPx.value = px;
    }
    if (this._paused) {
      this._displayUniforms.uBuffer.value = this._buffer.read.texture;
      return;
    }

    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;

    // The injection first, into its own target: the feedback pass reads it at
    // the folded coordinate and is the only thing that writes the buffer.
    renderer.setRenderTarget(this._source.write);
    renderer.autoClear = false;
    renderer.clear(true, false, false);
    renderer.render(this._injectScene, this._ortho);

    this._feedbackUniforms.uPrevious.value = this._buffer.read.texture;
    this._feedbackUniforms.uSource.value = this._source.read.texture;
    this._feedbackUniforms.uDecay.value = this._fading ? FADE_DECAY : this._decay;
    this._feedbackUniforms.uSeed.value = (this._frameCount * 13.13) % 64;
    renderer.setRenderTarget(this._buffer.write);
    renderer.render(this._feedbackScene, this._ortho);

    this._frameCount++;
    if (!this._fading && this._frameCount % GOVERN_EVERY_FRAMES === 0) this._measureLuminance(renderer);

    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;

    this._buffer.swap();
    this._displayUniforms.uBuffer.value = this._buffer.read.texture;

    if (this._lumEl && this._frameCount % 10 === 0) {
      this._lumEl.textContent = 'kaleido  lum ' + this._lastLum.toFixed(3)
        + '\n         decay ' + this._decay.toFixed(3)
        + '\n         order ' + this._order
        + (this._governorHot ? '\n         GOVERNOR ACTIVE' : '');
    }
  }

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length)
      ? palette.vividColors : [0x39a0ff, 0xff3ba7, 0xffd23b];
    for (let i = 0; i < 3; i++) this._palette[i].set(vivid[i % vivid.length]);
    const hsl = { h: 0, s: 0, l: 0 };
    this._palette[0].getHSL(hsl);
    this._hueDir = hsl.h >= 0.5 ? -1 : 1;
  }

  // The source wedge: the half-sector the fold reads from. Anything injected
  // outside it is simply discarded on the next pass, so everything that is
  // meant to be MULTIPLIED by the symmetry has to be placed inside it.
  _wedgeAngle(t) {
    const sector = (Math.PI * 2) / this._order;
    return this._rotation + t * sector * 0.5;
  }

  _spawnShape() {
    const kind = Math.floor(Math.random() * 3);
    const color = this._palette[Math.floor(Math.random() * 3)];
    // Placed inside the wedge, at a random radius along it.
    const a = this._wedgeAngle(0.15 + Math.random() * 0.7);
    const rad = 0.10 + Math.random() * 0.26;
    const mesh = kind === 0 ? this._bar : (kind === 1 ? this._arc : this._tri);
    for (const m of this._shapes) m.visible = false;
    mesh.visible = true;
    if (kind === 1) {
      // The arc is drawn about its own centre, so it sits ON the centre and
      // is rotated into the wedge.
      mesh.position.set(0, 0, 0);
      mesh.rotation.z = a;
    } else {
      mesh.position.set(Math.cos(a) * rad, Math.sin(a) * rad, 0);
      mesh.rotation.z = a;
    }
    mesh.material.uniforms.uColor.value.copy(color);
    mesh.material.uniforms.uOpacity.value = 1.0;
    this._shapeUntil = this._time + SHAPE_LIFE_S;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;
    this._time += deltaTime;

    const director = profile.director;
    const bands = director && director.bands;
    const graded = !!(director && director.hasReliableGrid) && bands;
    const low = graded && bands.low != null ? clamp01(bands.low) : 0.5;
    const high = graded && bands.high != null ? clamp01(bands.high) : 0.5;
    const k = 1 - Math.exp(-deltaTime / BAND_TAU);
    this._lowSmooth += (low - this._lowSmooth) * k;
    this._highSmooth += (high - this._highSmooth) * k;
    this._bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || null;
    const spb = this._bpm ? 60 / this._bpm : 0.5;

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }

    // Track change: Echo's 1.2s fade toward black, never a clear.
    if (profile.trackId !== this._lastTrackId) {
      const first = this._lastTrackId === undefined;
      this._lastTrackId = profile.trackId;
      if (!first) this._fadeUntil = this._time + TRACK_FADE_S;
    }
    const fading = this._time < this._fadeUntil;
    this._fading = fading;

    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) {
        const self = this;
        loadArtworkTexture(profile.artworkUrl, { wrap: THREE.ClampToEdgeWrapping, mipmaps: false },
          function (tex) {
            if (self._artTexture) self._artTexture.dispose();
            self._artTexture = tex;
            self._artMesh.material.map = tex;
            self._artMesh.material.needsUpdate = true;
            self._artMesh.visible = true;
          },
          function () {});
      } else {
        this._artMesh.visible = false;
      }
    }

    // --- drops: the mandala blows outward ------------------------------------
    // director.nextEvent carries only {type, score, msUntil} and flips to the
    // following mark the instant one passes, so the drop is latched here in
    // this visualizer's own clock - the same pattern swarm.js uses.
    const nextEvent = director && director.nextEvent;
    if (nextEvent && (nextEvent.type === 'drop' || nextEvent.type === 'rise')
      && this._dropUntil < this._time) {
      const until = nextEvent.msUntil / 1000;
      if (until > 0 && until <= 0.12) {
        this._dropOrderBefore = this._order;
        this._setOrder(DROP_ORDER);
        this._dropUntil = this._time + 2 * spb;
      }
    }
    const dropping = this._time < this._dropUntil;
    if (!dropping && this._dropOrderBefore != null) {
      this._setOrder(this._dropOrderBefore);
      this._dropOrderBefore = null;
    }

    // --- musical events -------------------------------------------------------
    const beatIndex = profile.beatIndex;
    if (this._coldStart && !fading && this._time >= this._nextSeedAt) {
      this._nextSeedAt = this._time + 0.7;
      this._spawnShape();
    }
    if (beatIndex != null && this._lastBeatIndex != null && beatIndex !== this._lastBeatIndex && !fading) {
      this._coldStart = false;
      this._spawnShape();
      const barIndex = profile.barIndex;
      if (barIndex != null && barIndex !== this._lastBarIndex) {
        // The bar re-seeds the pattern with the cover at full strength for one
        // frame, and steps the rotation on by a notch.
        this._reseedUntil = this._time + RESEED_LIFE_S;
        this._stepRotation(spb);
      }
      this._lastBarIndex = profile.barIndex;
    } else if (this._lastBarIndex === null) {
      this._lastBarIndex = profile.barIndex;
    }
    this._lastBeatIndex = beatIndex;

    // --- phrases: a new order of symmetry, as a CUT --------------------------
    const phraseIndex = profile.phraseIndex;
    if (this._forcedOrder == null && !dropping
      && phraseIndex != null && this._lastPhraseIndex != null && phraseIndex !== this._lastPhraseIndex) {
      const progress = profile.duration > 0 ? clamp01(profile.progress)
        : (director && director.intensity != null ? director.intensity : 0.5);
      // Opens and closes on the orders that read as a kaleidoscope; the middle
      // of the track gets everything the tier can do.
      const mid = progress > 0.2 && progress < 0.8;
      const pool = this._layout.orders.filter((o) => mid || CALM_ORDERS.indexOf(o) !== -1);
      const choices = (pool.length ? pool : this._layout.orders).filter((o) => o !== this._order);
      if (choices.length) this._setOrder(choices[Math.floor(Math.random() * choices.length)]);
    }
    this._lastPhraseIndex = phraseIndex;

    // Retire expired injected content.
    // The flash is over - but with no cover the shapes are the loop's ONLY
    // source, and a source present for an eighth of each beat leaves the loop
    // sub-critical between flashes: the floor subtraction wins and zero is an
    // absorbing state. Measured on the Pi, 480 iterations with no artwork:
    // frame mean 0.000. The last shape is therefore held, faintly, until the
    // next beat. With a cover the cover is the continuous source and this
    // never applies. Tessel carries the same guard.
    if (this._time >= this._shapeUntil) {
      const hold = !this._artTexture && !fading;
      for (const m of this._shapes) {
        if (hold && m.visible) m.material.uniforms.uOpacity.value = SHAPE_HOLD_OPACITY;
        else m.visible = false;
      }
    }
    const reseeding = this._time < this._reseedUntil;
    this._artMesh.visible = !!this._artTexture && !fading;
    this._artMesh.material.opacity = reseeding ? ART_RESEED_OPACITY : ART_OPACITY;
    if (fading) { for (const m of this._shapes) m.visible = false; }

    // --- the transforms -------------------------------------------------------
    // ZOOM breathes on the beat: it digs inward on the downstroke and relaxes.
    // beatPhase drives it directly rather than a free-running oscillator, so
    // the breath cannot drift off the grid.
    const bp = profile.beatPhase != null ? profile.beatPhase : (this._time / spb) % 1;
    this._beatPhase = bp;
    const dig = Math.exp(-bp * ZOOM_DECAY);
    const swing = 1 + (this._lowSmooth - 0.5) * 2 * (ZOOM_BAND_SWING / (ZOOM_BEAT - ZOOM_REST));
    let zoom = ZOOM_REST + (ZOOM_BEAT - ZOOM_REST) * dig * swing;
    // A drop inverts the breath: the pattern blows outward for two beats.
    if (dropping) zoom = ZOOM_REST - (ZOOM_BEAT - ZOOM_REST) * (0.6 + 0.4 * dig);
    this._feedbackUniforms.uZoom.value = zoom;

    // ROTATION moves in notches, one per bar, glided over a beat.
    if (this._rotT < 1) {
      this._rotT = Math.min(1, this._rotT + deltaTime / this._rotDur);
      this._rotation = lerp(this._rotFrom, this._rotTo, this._rotT * this._rotT * (3 - 2 * this._rotT));
    }
    this._feedbackUniforms.uRotation.value = this._rotation;

    // The CENTRE drifts, very slowly. A perfectly fixed centre gives a pattern
    // that is too perfect to look at for long.
    const ca = (this._time / CENTRE_PERIOD_S) * Math.PI * 2;
    this._feedbackUniforms.uCentre.value.set(
      Math.cos(ca) * CENTRE_DRIFT, Math.sin(ca * 0.73) * CENTRE_DRIFT);

    // The cover, off centre so the wedge crosses a different part of it as the
    // rotation steps round.
    const aa = this._rotation + 0.35;
    this._artMesh.position.set(Math.cos(aa) * ART_OFFSET, Math.sin(aa) * ART_OFFSET, 0);

    // Hue drift - stronger than Echo's, which is what lays the concentric
    // rings of colour down.
    this._hueAngle = HUE_SHIFT * (0.6 + 0.8 * this._highSmooth) * Math.PI * 2 * this._hueDir;
    hueMatrix(this._hueAngle, this._feedbackUniforms.uHueMat.value);
  }

  // A CUT, not a transition. On a feedback loop the change of order reads as
  // the pattern being torn up and re-formed, which settles in about a second -
  // and that is exactly the phrase event wanted.
  _setOrder(order) {
    this._order = order;
    this._feedbackUniforms.uOrder.value = order;
  }

  _stepRotation(spb) {
    const sector = (Math.PI * 2) / this._order;
    this._rotFrom = this._rotation;
    this._rotTo = this._rotation + sector / ROT_STEPS_PER_SECTOR;
    this._rotT = 0;
    this._rotDur = Math.max(0.12, ROT_GLIDE_BEATS * spb);
  }

  dispose() {
    this._scene.remove(this._displayMesh);
    this._displayMesh.geometry.dispose();
    this._displayMesh.material.dispose();
    this._feedbackQuad.geometry.dispose();
    this._feedbackQuad.material.dispose();
    for (const m of this._shapes.concat([this._artMesh])) {
      m.geometry.dispose();
      m.material.dispose();
    }
    this._shapeMaterials.length = 0;
    this._lumQuad.geometry.dispose();
    this._lumQuad.material.dispose();
    this._lumTarget.dispose();
    this._buffer.dispose();
    this._source.dispose();
    if (this._artTexture) this._artTexture.dispose();
    if (this._lumEl && this._lumEl.parentNode) this._lumEl.parentNode.removeChild(this._lumEl);
  }
}
